import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Wallet, TrendingUp, FileText, Calendar, PieChart, ChevronDown, LogOut, FolderOpen, DollarSign, Users, Menu, X, User, Tag, Settings, Moon, Sun, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';

const NAV = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/transactions', icon: FileText, label: 'Transações' },
  { path: '/pots', icon: Wallet, label: 'Potes' },
  { path: '/accounts', icon: TrendingUp, label: 'Contas' },
  { path: '/distribution', icon: PieChart, label: 'Distribuição' },
  { path: '/payable-receivable', icon: Calendar, label: 'A Pagar/Receber' },
  { path: '/revenue-sources', icon: DollarSign, label: 'Fontes' },
  { path: '/entities', icon: Users, label: 'Pessoas' },
  { path: '/tags', icon: Tag, label: 'Tags' },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentProject, projects, setCurrentProject } = useProject();
  const { theme, toggle } = useTheme();
  const [showProj, setShowProj] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [showMobile, setShowMobile] = useState(false);

  const nav = user?.role === 'admin' ? [...NAV, { path: '/admin', icon: ShieldCheck, label: 'Admin' }] : NAV;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 transition-colors">
      <nav className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200/50 dark:border-slate-800 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-indigo-600 to-blue-500 p-2 rounded-xl shadow-md">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent">D'Pots</h1>
            </div>
            <div className="hidden lg:flex items-center space-x-3">
              {currentProject && (
                <div className="relative">
                  <button onClick={() => setShowProj(!showProj)} className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                    <FolderOpen className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 max-w-[160px] truncate">{currentProject.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  </button>
                  {showProj && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowProj(false)} />
                      <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-2 z-50">
                        {projects.map((p) => (
                          <button key={p.id} onClick={() => { setCurrentProject(p); setShowProj(false); }} className={`w-full text-left px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 ${p.id===currentProject.id?'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400':'text-slate-700 dark:text-slate-300'}`}>
                            <div className="font-medium">{p.name}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">{p.type === 'business' ? 'Empresa' : 'Pessoal'}</div>
                          </button>
                        ))}
                        <div className="border-t border-slate-200 dark:border-slate-700 mt-2 pt-2">
                          <button onClick={() => { setShowProj(false); navigate('/projects/manage'); }} className="w-full text-left px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-700">
                            Gerenciar Projetos
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              <NotificationBell />
              <button onClick={toggle} aria-label="Alternar tema"
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
              </button>
              <div className="relative">
                <button onClick={() => setShowUser(!showUser)} className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.email} className="w-8 h-8 rounded-full border-2 border-indigo-200 dark:border-indigo-800" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">{user?.email?.[0]?.toUpperCase()}</div>
                  )}
                  <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                </button>
                {showUser && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUser(false)} />
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-2 z-50">
                      <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                        <div className="font-medium text-slate-900 dark:text-slate-100">{user?.name || user?.email}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</div>
                      </div>
                      <button onClick={() => { setShowUser(false); navigate('/profile'); }} className="w-full text-left px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2">
                        <User className="w-4 h-4" /><span>Perfil</span>
                      </button>
                      <button onClick={() => { setShowUser(false); navigate('/projects/manage'); }} className="w-full text-left px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2">
                        <Settings className="w-4 h-4" /><span>Gerenciar Projetos</span>
                      </button>
                      <div className="border-t border-slate-200 dark:border-slate-700 my-1"></div>
                      <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2">
                        <LogOut className="w-4 h-4" /><span>Sair</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-1 lg:hidden">
              <NotificationBell />
              <button onClick={toggle} aria-label="Alternar tema"
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
              </button>
              <button onClick={() => setShowMobile(!showMobile)} aria-label="Menu" data-testid="mobile-menu-toggle"
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                {showMobile ? <X className="w-6 h-6 text-slate-600 dark:text-slate-300" /> : <Menu className="w-6 h-6 text-slate-600 dark:text-slate-300" />}
              </button>
            </div>
          </div>
          <div className="hidden lg:flex items-center space-x-1 pb-3 overflow-x-auto">
            {nav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${isActive?'bg-gradient-to-r from-indigo-600 to-blue-500 text-white shadow-md':'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'}`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
        {showMobile && (
          <div className="lg:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 max-h-[80vh] overflow-y-auto" data-testid="mobile-drawer">
            {/* User + project info */}
            {user && (
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
                {user.picture ? (
                  <img src={user.picture} alt={user.email} className="w-10 h-10 rounded-full border-2 border-indigo-200 dark:border-indigo-800" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold">{user.email?.[0]?.toUpperCase()}</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{user.name || user.email}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</div>
                </div>
              </div>
            )}

            {/* Project selector */}
            {projects.length > 0 && (
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                <div className="text-[10px] uppercase text-slate-500 mb-1.5 font-semibold tracking-wide">Projeto atual</div>
                <div className="grid gap-1.5">
                  {projects.map((p) => (
                    <button key={p.id} onClick={() => { setCurrentProject(p); setShowMobile(false); }}
                      data-testid={`mobile-project-${p.id}`}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition ${p.id === currentProject?.id ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                      <div className={`font-medium text-sm ${p.id === currentProject?.id ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-200'}`}>{p.name}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">{p.type === 'business' ? 'Empresa' : 'Pessoal'}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Nav links */}
            <div className="px-4 py-2 space-y-1">
              {nav.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setShowMobile(false)}
                    data-testid={`mobile-nav-${item.path.replace(/[^a-z0-9]/gi, '-') || 'home'}`}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg ${isActive ? 'bg-gradient-to-r from-indigo-600 to-blue-500 text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                    <Icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
              <button onClick={() => { setShowMobile(false); navigate('/profile'); }}
                className="w-full text-left px-4 py-3 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex items-center space-x-3">
                <User className="w-5 h-5" /><span className="font-medium">Perfil</span>
              </button>
              <button onClick={() => { setShowMobile(false); navigate('/projects/manage'); }}
                className="w-full text-left px-4 py-3 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex items-center space-x-3">
                <Settings className="w-5 h-5" /><span className="font-medium">Gerenciar Projetos</span>
              </button>
              <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center space-x-3">
                <LogOut className="w-5 h-5" /><span className="font-medium">Sair</span>
              </button>
            </div>
          </div>
        )}
      </nav>
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8">{children}</main>
    </div>
  );
}
